import React, { useEffect, useMemo } from 'react';
import { createDebugLog } from '@ydistri/utils';
import { Badge, Popover } from 'antd';
import { Text } from '@ydistri/ds';
import {
  getMessageInfoFromValidatorSectionMessages,
  ValidatorStatus,
  validatorStatusBadgeColors,
  validatorStatusTextColors,
} from './validatorLib';
import { ValidatorButton } from './validatorStyledComponents';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../store';
import { setValidatorSectionMessageInfo, setValidatorStatus } from './validatorSlice';
import { useValidator } from './useValidator';
import ValidatorContent from './ValidatorContent';
import { AiOutlineCheck } from 'react-icons/ai';
import { LoadingOutlined } from '@ant-design/icons';
import { TemplateOrCalculationWithValue } from '../../../apis/api';

const debugLog = createDebugLog('Validator');

export interface ValidatorProps {
  templateOrCalculation: TemplateOrCalculationWithValue;
  onValidChange?: (valid: boolean) => void;
  size: 'small' | 'large';
}

const Validator: React.FC<ValidatorProps> = ({ templateOrCalculation, onValidChange, size }) => {
  const templateId = templateOrCalculation.id;

  const dispatch = useAppDispatch();
  const { isLoading, errorCount, warningCount, valid } = useValidator(templateOrCalculation);

  const modal = useSelector((state: ReduxState) => state.validator.modal);
  const status =
    useSelector((state: ReduxState) => state.validator.template[templateId]?.status) ??
    ValidatorStatus.INFO;
  const sectionMessages = useSelector(
    (state: ReduxState) => state.validator.template[templateId]?.sectionMessages,
  );

  useEffect(() => {
    onValidChange?.(valid);
  }, [valid, onValidChange]);

  useEffect(() => {
    if (!sectionMessages) return;
    debugLog('sectionMessages', sectionMessages);
    dispatch(
      setValidatorSectionMessageInfo({
        templateId,
        data: getMessageInfoFromValidatorSectionMessages(sectionMessages),
      }),
    );
  }, [dispatch, sectionMessages, templateId]);

  useEffect(() => {
    if (isLoading) {
      dispatch(setValidatorStatus({ templateId, status: ValidatorStatus.RUNNING }));
    } else if (errorCount > 0) {
      dispatch(setValidatorStatus({ templateId, status: ValidatorStatus.ERROR }));
    } else if (warningCount > 0) {
      dispatch(setValidatorStatus({ templateId, status: ValidatorStatus.WARNING }));
    } else {
      dispatch(setValidatorStatus({ templateId, status: ValidatorStatus.INFO }));
    }
  }, [dispatch, isLoading, errorCount, warningCount, templateId]);

  const icon = useMemo(() => {
    if (isLoading) return <LoadingOutlined />;
    if (errorCount === 0 && warningCount === 0) return <AiOutlineCheck />;
    return undefined;
  }, [isLoading, errorCount, warningCount]);

  const button = (
    <ValidatorButton $size={size}>
      <Text $size="large" $type={validatorStatusTextColors[status]}>
        Validator
      </Text>
      {icon && (
        <Text $size="large" $type={validatorStatusTextColors[status]}>
          {icon}
        </Text>
      )}
      {!icon && errorCount > 0 && (
        <Badge color={validatorStatusBadgeColors[ValidatorStatus.ERROR]} count={errorCount} />
      )}
      {!icon && warningCount > 0 && (
        <Badge color={validatorStatusBadgeColors[ValidatorStatus.WARNING]} count={warningCount} />
      )}
    </ValidatorButton>
  );

  // Unmounting the Popover is what closes an open one: empty content only stops it from opening.
  if (
    modal ||
    !sectionMessages ||
    getMessageInfoFromValidatorSectionMessages(sectionMessages).total === 0
  ) {
    return button;
  }

  return (
    <Popover content={<ValidatorContent templateId={templateId} />} placement="bottom">
      {button}
    </Popover>
  );
};

export default Validator;
